import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, Zap, Shield,
  ArrowRight, ArrowLeft, CheckCircle,
  GitBranch, RefreshCw, Database, Lock, Server,
  BarChart3, AlertTriangle, Cpu, Network, FileCheck
} from 'lucide-react';
import { ContactSection } from '../components/ContactSection';

/* ─── Primitives ──────────────────────────────── */
type Particle = { x: number; y: number; vx: number; vy: number; r: number; pulse: number };

const ParticleCanvas = ({ color = '#ff5d22', count = 55 }: { color?: string; count?: number }) => {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let w: number, h: number, pts: Particle[], raf: number;
    const init = () => {
      w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight;
      pts = Array.from({ length: count }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - .5) * .45, vy: (Math.random() - .5) * .45,
        r: Math.random() * 1.6 + .5, pulse: Math.random() * Math.PI * 2,
      }));
    };
    const [r, g, b] = [1, 3, 5].map((_, i) => parseInt(color.slice(1 + i * 2, 3 + i * 2), 16));
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.pulse += .02;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
      });
      for (let i = 0; i < pts.length; i++) for (let j = i + 1; j < pts.length; j++) {
        const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
        if (d < 115) {
          ctx.beginPath(); ctx.strokeStyle = `rgba(${r},${g},${b},${(1 - d / 115) * .2})`;
          ctx.lineWidth = .65; ctx.moveTo(pts[i].x, pts[i].y); ctx.lineTo(pts[j].x, pts[j].y); ctx.stroke();
        }
      }
      pts.forEach(p => {
        const pr = p.r * (1 + .28 * Math.sin(p.pulse));
        const g2 = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, pr * 3);
        g2.addColorStop(0, `rgba(${r},${g},${b},.88)`); g2.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.beginPath(); ctx.arc(p.x, p.y, pr * 3, 0, Math.PI * 2); ctx.fillStyle = g2; ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    const ro = new ResizeObserver(init); ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [color, count]);
  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />;
};

const Orb = ({ style, color, size, dur, delay = 0 }: { style?: React.CSSProperties; color: string; size: number; dur: number; delay?: number }) => (
  <div style={{
    position: 'absolute', borderRadius: '50%', filter: `blur(${size * .34}px)`,
    width: size, height: size, background: color, opacity: .13,
    animation: `srOrbFloat ${dur}s ease-in-out ${delay}s infinite`, ...style
  }} />
);

const Reveal = ({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: .08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{
      opacity: vis ? 1 : 0, transform: vis ? 'translateY(0) scale(1)' : 'translateY(26px) scale(.97)',
      transition: `opacity .62s ease ${delay}s, transform .62s ease ${delay}s`,
    }}>{children}</div>
  );
};

const TCard = ({ children, className = '' }: { children: React.ReactNode; className?: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <div ref={ref} className={className} style={{ transition: 'transform .26s ease', transformStyle: 'preserve-3d' }}
      onMouseMove={e => {
        if (!ref.current) return;
        const r = ref.current.getBoundingClientRect();
        ref.current.style.transform = `perspective(800px) rotateY(${((e.clientX - r.left) / r.width - .5) * 11}deg) rotateX(${((e.clientY - r.top) / r.height - .5) * -11}deg) translateY(-4px)`;
      }}
      onMouseLeave={() => { if (ref.current) ref.current.style.transform = 'perspective(800px) rotateY(0) rotateX(0) translateY(0)'; }}>
      {children}
    </div>
  );
};

const FaqItem = ({ q, a, i }: { q: string; a: string; i: number }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={i * .07}>
      <div onClick={() => setOpen(!open)} className="cursor-pointer mb-3 rounded-2xl border transition-all duration-300"
        style={{
          background: open ? 'linear-gradient(135deg,#0f0f0f,#191919)' : '#fff',
          borderColor: open ? 'rgba(227,30,36,.4)' : '#e5e7eb',
          boxShadow: open ? '0 8px 34px rgba(227,30,36,.1)' : '0 1px 6px rgba(0,0,0,.04)',
          padding: '14px 18px'
        }}>
        <div className="flex items-center justify-between gap-4">
          <span style={{ fontWeight: 700, fontSize: 13, color: open ? '#fff' : '#111' }}>{q}</span>
          <div style={{
            width: 24, height: 24, borderRadius: '50%', background: open ? '#ff5d22' : '#f3f4f6',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            transition: 'all .26s ease', transform: open ? 'rotate(45deg)' : 'none'
          }}>
            <svg width={9} height={9} viewBox="0 0 14 14" fill="none" stroke={open ? '#fff' : '#555'} strokeWidth={2.5}><path d="M7 1v12M1 7h12" /></svg>
          </div>
        </div>
        <div style={{ overflow: 'hidden', maxHeight: open ? 220 : 0, transition: 'max-height .36s ease', marginTop: open ? 10 : 0 }}>
          <p style={{ fontSize: 12.5, lineHeight: 1.65, color: 'rgba(255,255,255,.68)', margin: 0 }}>{a}</p>
        </div>
      </div>
    </Reveal>
  );
};

/* ─── Spec data ──────────────────────────────────── */
const SPECS = [
  {
    slug: 'lifecycle-engine',
    title: 'Commerce Lifecycle Engine',
    tagline: 'Automate cloud commerce workflows from lead generation to service delivery.',
    icon: Activity,
    overview: `The Commerce Lifecycle Engine powers every customer, partner, and vendor interaction across the AppGallop cloud distribution ecosystem. From lead qualification and opportunity management to quoting, ordering, fulfillment, and support resolution, every process is automated through a scalable workflow architecture that accelerates operations while maintaining complete visibility and governance.`,
    bullets: [
      'Lead-to-Order automation across customer and partner journeys',
      'Quote creation, approvals, and commercial workflow management',
      'Subscription lifecycle orchestration and contract governance',
      'Ticket-to-Resolution automation with SLA monitoring',
      'Partner onboarding and channel workflow automation',
      'End-to-end audit visibility across all commerce operations'
    ],
    stats: [
      { v: '60%', l: 'Reduced Operational Effort' },
      { v: '10x', l: 'Process Scalability' },
      { v: '100%', l: 'Workflow Traceability' },
    ],
    howItWorks: [
      { step: '01', title: 'Event Ingestion', desc: 'Lifecycle triggers arrive from CRM, CPQ, or external APIs and are normalised into structured workflow events.' },
      { step: '02', title: 'BPM Routing', desc: 'The BPM engine matches each event to the configured workflow template and routes it to the correct execution path.' },
      { step: '03', title: 'Step Execution', desc: 'Each workflow step fires its action,  provisioning call, approval request, notification, or data sync with retry logic on failure.' },
      { step: '04', title: 'SLA Tracking', desc: 'Timers activate per step. Breaches trigger escalation paths defined in the workflow without human intervention.' },
    ],
    techSpecs: [
      { label: 'Engine type', value: 'Event-driven BPM (BPEL-compatible)' },
      { label: 'Trigger sources', value: 'REST, Webhooks, Kafka, CRM, CPQ' },
      { label: 'Workflow formats', value: 'BPMN 2.0, JSON DSL, Visual Designer' },
      { label: 'Concurrency', value: 'Parallelised up to 50k concurrent workflows' },
      { label: 'Audit log', value: 'Immutable, encrypted, queryable by event ID' },
      { label: 'Integrations', value: 'Salesforce, SAP, ServiceNow, Freshdesk, NetSuite' },
    ],
    capabilities: [
      { icon: GitBranch, title: 'Parallel Workflow Branches', desc: 'Split flows into concurrent tracks and merge on completion ideal for multi-vendor provisioning.' },
      { icon: RefreshCw, title: 'Retry & Fallback Logic', desc: 'Configurable retry policies and fallback paths prevent single points of failure from stalling workflows.' },
      { icon: BarChart3, title: 'Real-time Flow Analytics', desc: 'Live dashboards show throughput, step latency, SLA status, and bottleneck identification across all workflows.' },
      { icon: Lock, title: 'Governance & Compliance', desc: 'Every workflow step is policy-gated. Approval matrices and compliance checks are embedded, not bolted on.' },
    ],
    faqs: [
      { q: 'Can workflows be modified without downtime?', a: 'Yes. AppGallop supports hot-reload of workflow definitions. Changes to templates apply to new instances immediately without affecting in-flight workflows.' },
      { q: 'How does the engine handle long-running workflows spanning days?', a: 'Workflow state is persisted in a durable store. Long-running processes resume from their last checkpoint after any interruption, with no data loss.' },
      { q: 'Can we build custom workflow steps?', a: 'Absolutely. The step library is extensible via REST callout definitions, custom scripts, or pre-built connectors. No platform engineering is required.' },
    ],
  },
  {
    slug: 'service-orchestration',
    title: 'Cloud Service Orchestration',
    tagline: 'Provision, activate, and manage cloud services across vendors and platforms.',
    icon: Zap,
    overview: `The Cloud Service Orchestration layer connects commercial operations with cloud providers, vendors, and downstream fulfillment systems. It automates provisioning, subscription lifecycle management, activation workflows, and service delivery across multi-cloud environments, ensuring customers receive services faster with complete operational transparency.`,
    bullets: [
      'Lead-to-Order automation across customer and partner journeys',
      'Quote creation, approvals, and commercial workflow management',
      'Subscription lifecycle orchestration and contract governance',
      'Ticket-to-Resolution automation with SLA monitoring',
      'Partner onboarding and channel workflow automation',
      'End-to-end audit visibility across all commerce operations'
    ],
    stats: [
      { v: 'Multi-Cloud', l: 'AWS Azure GCP Ready' },
      { v: '24x7', l: 'Automated Fulfillment' },
      { v: '99.99%', l: 'Service Availability' },
    ],
    howItWorks: [
      { step: '01', title: 'Order Decomposition', desc: 'Complex orders are broken into atomic provisioning tasks mapped to specific vendor or system endpoints.' },
      { step: '02', title: 'Dependency Resolution', desc: 'The orchestrator resolves service dependencies and sequences tasks in the correct activation order.' },
      { step: '03', title: 'API Dispatch', desc: 'Each task is dispatched to the target system API with authentication, payload formatting, and rate-limit management handled automatically.' },
      { step: '04', title: 'Status Reconciliation', desc: 'Async responses are polled or pushed back via webhook. State is reconciled and surfaced to the order management layer in real time.' },
    ],
    techSpecs: [
      { label: 'API protocols', value: 'REST, SOAP, GraphQL, gRPC, Webhooks' },
      { label: 'Provisioning modes', value: 'Synchronous, Asynchronous, Batch' },
      { label: 'Connector library', value: '50+ pre-built (AWS, Azure, GCP, Cisco, Ericsson…)' },
      { label: 'Retry strategy', value: 'Exponential back-off with configurable ceiling' },
      { label: 'Rate limiting', value: 'Per-vendor throttling with queue overflow management' },
      { label: 'Security', value: 'OAuth 2.0, mTLS, API key rotation, vault integration' },
    ],
    capabilities: [
      { icon: Network, title: 'Multi-Cloud Provisioning', desc: 'Activate services across AWS, Azure, Google Cloud, and private infrastructure from a single orchestration call.' },
      { icon: Server, title: 'NMS / OSS Integration', desc: 'Pre-built adapters for Ericsson, Nokia, Cisco, and custom NMS platforms enable telco-grade network provisioning.' },
      { icon: AlertTriangle, title: 'Fault Isolation & Rollback', desc: 'When a downstream API fails, the orchestrator executes compensation actions to restore the previous consistent state.' },
      { icon: Cpu, title: 'Intelligent Load Distribution', desc: 'Provisioning tasks are distributed across available execution workers with priority queuing for SLA-critical orders.' },
    ],
    faqs: [
      { q: 'What happens if a downstream API is unavailable?', a: 'AppGallop activates the configured fallback path either an alternative vendor, a manual queue handoff, or a hold-and-retry with operator notification.' },
      { q: 'Can we add custom vendor connectors?', a: 'Yes. The connector SDK allows you to define new endpoints using a YAML specification. Custom connectors are treated identically to native ones in the orchestration engine.' },
      { q: 'How is provisioning status communicated to end customers?', a: 'Status updates are pushed to the self-serve portal and optionally via email, SMS, or webhook to your CRM or support system in real time.' },
    ],
  },
  {
    slug: 'financial-reconciliation-layer',
    title: 'Revenue & Financial Reconciliation',
    tagline: 'Reconcile subscriptions, billing, commissions, and revenue with complete accuracy.',
    icon: Shield,
    overview: `The Revenue and Financial Reconciliation layer ensures complete financial accuracy across subscriptions, usage-based billing, commissions, partner settlements, taxation, invoicing, and ERP integrations. Every transaction is validated, reconciled, and tracked to provide audit-ready financial operations for cloud distributors.`,
    bullets: [
      'Subscription billing and revenue reconciliation',
      'Automated commission and partner settlement management',
      'Multi-currency and multi-entity accounting support',
      'ERP integration with SAP, Oracle, NetSuite, and Dynamics',
      'Tax calculation and compliance automation',
      'Complete audit trail for every financial transaction'
    ],
    stats: [
      { v: '100%', l: 'Revenue Traceability' },
      { v: '95%', l: 'Faster Financial Close' },
      { v: '0', l: 'Revenue Leakage' },
    ],
    howItWorks: [
      { step: '01', title: 'Transaction Capture', desc: 'Every billable event invoice line, usage charge, adjustment, or payment is captured with full context (product, partner, region, contract).' },
      { step: '02', title: 'Rule Engine Matching', desc: 'The GL rule engine matches each transaction to the correct account code based on product type, entity, jurisdiction, and revenue schedule.' },
      { step: '03', title: 'Revenue Recognition', desc: 'Recognised vs deferred revenue is calculated per ASC 606 / IFRS 15 policy and posted to the correct period automatically.' },
      { step: '04', title: 'ERP Sync', desc: 'Journal entries are generated and pushed to the target ERP in real time or on a configurable batch cycle with reconciliation confirmation.' },
    ],
    techSpecs: [
      { label: 'Standards', value: 'ASC 606, IFRS 15, GAAP-aligned' },
      { label: 'ERP connectors', value: 'SAP S/4HANA, Oracle ERP, NetSuite, Dynamics 365' },
      { label: 'Tax engines', value: 'Avalara, Vertex, native GST/VAT/TDS rules' },
      { label: 'Currencies', value: '160+ with real-time FX conversion' },
      { label: 'Certifications', value: 'SOC 2 Type II, ISO 27001, GDPR' },
      { label: 'Audit trail', value: 'Immutable per-transaction ledger entries' },
    ],
    capabilities: [
      { icon: Database, title: 'Multi-Entity Chart of Accounts', desc: 'Support multiple legal entities each with independent CoA structures, intercompany elimination, and consolidated reporting.' },
      { icon: FileCheck, title: 'Automated Period Close', desc: 'Period-end reconciliation runs automatically with exception flagging, approval gates, and auditor-ready export packages.' },
      { icon: Lock, title: 'Immutable Audit Ledger', desc: 'Every GL posting is written to an append-only audit ledger. Entries cannot be altered corrections require a new offsetting entry.' },
      { icon: BarChart3, title: 'Revenue Intelligence Dashboards', desc: 'Real-time dashboards show recognised vs deferred revenue, aging, period variance, and compliance status across all entities.' },
    ],
    faqs: [
      { q: 'How does the platform handle revenue recognition for multi-element arrangements?', a: 'AppGallop applies SSP (Standalone Selling Price) allocation rules per ASC 606 guidance. Each performance obligation in a bundled contract gets its own recognition schedule.' },
      { q: 'Can we configure custom GL mapping rules?', a: 'Yes. The rule engine supports attribute-based mapping any combination of product, partner type, region, entity, and contract term can be used to derive the target account code.' },
      { q: 'How are corrections handled without breaking the audit trail?', a: 'All corrections use standard double-entry accounting the original entry remains intact and a new offsetting journal is posted with a reference to the correction reason.' },
    ],
  },
];

/* ─── Sticky nav ──────────────────────────────── */
const StickyNav = ({ active }: { active: string }) => (
  <div className="sticky top-[72px] z-40 bg-white/90 backdrop-blur-xl border-b border-black/5 shadow-sm">
    <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto py-2">
      {SPECS.map(s => (
        <a key={s.slug} href={`#${s.slug}`}
          className={`flex-shrink-0 px-4 py-2 rounded-full text-[9px] font-black uppercase tracking-widest transition-all duration-200 ${active === s.slug ? 'bg-[#ff5d22] text-white shadow-md shadow-[#ff5d22]/20' : 'text-black/50 hover:text-black hover:bg-slate-100'}`}>
          {s.title}
        </a>
      ))}
    </div>
  </div>
);

/* ─── Spec section ────────────────────────────── */
const SpecSection = ({ spec, idx }: { spec: typeof SPECS[0]; idx: number }) => {
  const isEven = idx % 2 === 0;
  const Icon = spec.icon;

  return (
    <section id={spec.slug} className={`relative overflow-hidden scroll-mt-[120px] ${isEven ? 'bg-white' : 'bg-slate-950'}`}>
      <style>{`@keyframes srOrbFloat{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(36px,-26px) scale(1.06)}66%{transform:translate(-20px,16px) scale(.95)}}`}</style>

      <div className={`absolute inset-0 ${isEven ? 'bg-[radial-gradient(rgba(0,0,0,0.022)_1px,transparent_1px)] bg-[length:28px_28px]' : 'bg-[radial-gradient(rgba(255,255,255,0.028)_1px,transparent_1px)] bg-[length:28px_28px]'}`} />
      {!isEven && <ParticleCanvas color="#ff5d22" count={40} />}
      <Orb style={{ top: '-4%', right: '-5%' }} color="#ff5d22" size={isEven ? 240 : 320} dur={isEven ? 19 : 23} />
      {!isEven && <Orb style={{ bottom: '-8%', left: '-4%' }} color="#3b82f6" size={260} dur={25} delay={4} />}

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20">

        {/* ── TOP: hero text + stats ── */}
        <div className={`grid lg:grid-cols-2 gap-16 items-center mb-16`}>
          <div className={isEven ? '' : 'order-last lg:order-first'}>
            <Reveal>
              <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest mb-5 border ${isEven ? 'bg-[#ff5d22]/8 border-[#ff5d22]/20 text-[#ff5d22]' : 'bg-white/10 border-white/20 text-white/70'}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#ff5d22]" />
                System Resilience · {String(idx + 1).padStart(2, '0')}
              </span>
            </Reveal>

            <Reveal delay={0.07}>
              <h2 className={`text-3xl md:text-4xl lg:text-[2.5rem] font-black tracking-tighter leading-[1.08] mb-3 ${isEven ? 'text-slate-950' : 'text-white'}`}>
                {spec.title.split(' ').slice(0, -1).join(' ')}{' '}
                <span className="bg-gradient-to-r from-[#ff5d22] to-red-500 bg-clip-text text-transparent">
                  {spec.title.split(' ').slice(-1)[0]}
                </span>
              </h2>
            </Reveal>

            <Reveal delay={0.12}>
              <p className={`text-base font-semibold mb-5 ${isEven ? 'text-slate-500' : 'text-white/60'}`}>{spec.tagline}</p>
            </Reveal>

            <Reveal delay={0.16}>
              <p className={`text-sm leading-relaxed mb-7 ${isEven ? 'text-slate-600' : 'text-white/70'}`}>{spec.overview}</p>
            </Reveal>

            <Reveal delay={0.2}>
              <ul className="space-y-2.5 mb-8">
                {spec.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-md bg-[#ff5d22]/12 border border-[#ff5d22]/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle size={10} className="text-[#ff5d22]" />
                    </div>
                    <span className={`text-[13px] font-medium leading-snug ${isEven ? 'text-slate-700' : 'text-white/75'}`}>{b}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.24}>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" className="px-6 py-2.5 rounded-lg bg-[#ff5d22] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#d41820] transition-all shadow-lg hover:shadow-xl hover:shadow-[#ff5d22]/20 flex items-center gap-2">
                  Book a Demo <ArrowRight size={13} />
                </Link>
                
              </div>
            </Reveal>
          </div>

          {/* stat cards */}
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {spec.stats.map((s, i) => (
                <Reveal key={i} delay={0.1 + i * .08}>
                  <TCard className={`rounded-2xl p-4 border text-center relative overflow-hidden ${isEven ? 'bg-white border-black/8 shadow-sm hover:shadow-md' : 'bg-white/5 border-white/10'}`}>
                    <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/50 to-transparent" />
                    <div className="text-2xl font-black text-[#ff5d22] leading-none">{s.v}</div>
                    <div className={`text-[9px] font-bold uppercase tracking-wide mt-1.5 leading-tight ${isEven ? 'text-slate-500' : 'text-white/50'}`}>{s.l}</div>
                  </TCard>
                </Reveal>
              ))}
            </div>

            {/* how it works mini card */}
            <Reveal delay={0.28}>
              <div className={`rounded-2xl p-5 border relative overflow-hidden ${isEven ? 'bg-gradient-to-br from-slate-50 to-white border-black/8 shadow-sm' : 'bg-gradient-to-br from-slate-900 to-slate-950 border-[#ff5d22]/20'}`}>
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/55 to-transparent" />
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-[#ff5d22]/12 border border-[#ff5d22]/25 flex items-center justify-center">
                    <Icon size={18} className="text-[#ff5d22]" />
                  </div>
                  <div className={`text-sm font-black ${isEven ? 'text-black' : 'text-white'}`}>{spec.title}</div>
                </div>
                <div className="space-y-2">
                  {spec.howItWorks.slice(0, 3).map((h, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <span className={`w-5 h-5 rounded text-[9px] font-black flex-shrink-0 flex items-center justify-center ${isEven ? 'bg-[#ff5d22]/10 text-[#ff5d22]' : 'bg-[#ff5d22]/20 text-[#ff5d22]'}`}>{i + 1}</span>
                      <span className={`text-xs font-medium ${isEven ? 'text-slate-600' : 'text-white/65'}`}>{h.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>

            <div>{spec.faqs.slice(0, 2).map((faq, i) => <FaqItem key={i} q={faq.q} a={faq.a} i={i} />)}</div>
          </div>
        </div>

        {/* ── How it works steps ── */}
        <Reveal delay={0.1}>
          <div className={`rounded-3xl p-8 border mb-14 ${isEven ? 'bg-slate-50 border-black/6' : 'bg-white/4 border-white/10'}`}>
            <div className={`text-[9px] font-black uppercase tracking-widest mb-6 ${isEven ? 'text-black/40' : 'text-white/40'}`}>How It Works</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {spec.howItWorks.map((h, i) => (
                <div key={i} className={`relative p-5 rounded-2xl border ${isEven ? 'bg-white border-black/6 shadow-sm' : 'bg-white/5 border-white/10'}`}>
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/40 to-transparent" />
                  <div className="text-[#ff5d22] text-[10px] font-black font-mono mb-2">{h.step}</div>
                  <div className={`text-sm font-black mb-2 ${isEven ? 'text-black' : 'text-white'}`}>{h.title}</div>
                  <div className={`text-xs leading-relaxed font-medium ${isEven ? 'text-slate-500' : 'text-white/55'}`}>{h.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* ── Capabilities grid ── */}
        <Reveal delay={0.14}>
          <div className="mb-14">
            <div className={`text-[9px] font-black uppercase tracking-widest mb-5 ${isEven ? 'text-black/40' : 'text-white/40'}`}>Key Capabilities</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {spec.capabilities.map((c, i) => (
                <TCard key={i} className={`rounded-2xl p-5 border group transition-all duration-300 ${isEven ? 'bg-white border-black/6 shadow-sm hover:border-[#ff5d22]/25 hover:shadow-lg' : 'bg-white/4 border-white/10 hover:bg-white/8'}`}>
                  <div className="w-10 h-10 rounded-xl bg-[#ff5d22]/10 border border-[#ff5d22]/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <c.icon size={18} className="text-[#ff5d22]" />
                  </div>
                  <div className={`text-sm font-black mb-2 ${isEven ? 'text-black' : 'text-white'}`}>{c.title}</div>
                  <div className={`text-xs leading-relaxed font-medium ${isEven ? 'text-slate-500' : 'text-white/55'}`}>{c.desc}</div>
                </TCard>
              ))}
            </div>
          </div>
        </Reveal>

        {/* ── Tech specs table ── */}
        <Reveal delay={0.18}>
          <div className={`rounded-3xl border overflow-hidden ${isEven ? 'border-black/6' : 'border-white/10'}`}>
            <div className={`px-6 py-4 border-b ${isEven ? 'bg-slate-50 border-black/6' : 'bg-white/5 border-white/10'}`}>
              <div className={`text-[9px] font-black uppercase tracking-widest ${isEven ? 'text-black/40' : 'text-white/40'}`}>Technical Specifications</div>
            </div>
            <div className={isEven ? 'bg-white' : 'bg-white/2'}>
              {spec.techSpecs.map((t, i) => (
                <div key={i} className={`flex items-center justify-between px-6 py-3.5 text-sm ${i < spec.techSpecs.length - 1 ? (isEven ? 'border-b border-black/4' : 'border-b border-white/6') : ''}`}>
                  <span className={`font-black text-[10px] uppercase tracking-wider ${isEven ? 'text-black/40' : 'text-white/40'}`}>{t.label}</span>
                  <span className={`font-semibold text-[12px] text-right max-w-xs ${isEven ? 'text-slate-700' : 'text-white/75'}`}>{t.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/18 to-transparent" />
    </section>
  );
};

/* ─── Page ────────────────────────────────────── */
export default function SystemResiliencePage() {
  const [activeSlug, setActiveSlug] = useState(SPECS[0].slug);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    SPECS.forEach(s => {
      const el = document.getElementById(s.slug);
      if (!el) return;
      const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setActiveSlug(s.slug); }, { rootMargin: '-38% 0px -55% 0px' });
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  return (
    <div className="bg-white overflow-x-hidden">
      <style>{`@keyframes srOrbFloat{0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(36px,-26px) scale(1.06)}66%{transform:translate(-20px,16px) scale(.95)}}`}</style>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden px-6 pt-20 pb-6 bg-white">
        <style>{`@keyframes srPulse{0%,100%{opacity:.6;transform:scale(1)}50%{opacity:1;transform:scale(1.15)}}`}</style>

        {/* subtle dot grid */}
        <div className="absolute inset-0 bg-[radial-gradient(rgba(0,0,0,0.028)_1px,transparent_1px)] bg-[length:26px_26px]" />
        <ParticleCanvas color="#ff5d22" count={50} />
        <Orb style={{ top: '-8%', right: '-5%' }} color="#ff5d22" size={380} dur={18} />
        <Orb style={{ bottom: '-12%', left: '-4%' }} color="#3b82f6" size={300} dur={22} delay={3} />

        <div className="relative z-20 max-w-7xl mx-auto w-full">

          {/* ── breadcrumb ── */}
          <Reveal>
            <div className="flex items-center gap-2 mb-6">
              <Link to="/" className="text-[9px] font-black uppercase tracking-widest text-black/35 hover:text-[#ff5d22] transition-colors flex items-center gap-1">
                <ArrowLeft size={10} /> Home
              </Link>                            
              <span className="text-black/20 text-[9px]">/</span>
              <span className="text-[9px] font-black uppercase tracking-widest text-[#ff5d22]">System Resilience</span>
            </div>
          </Reveal>

          {/* ── two-column layout ── */}
          <div className="grid lg:grid-cols-2 gap-8 items-start mb-6">

            {/* LEFT */}
            <div>
              <Reveal delay={0.04}>
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/6 text-slate-700 text-[9px] font-black uppercase tracking-widest mb-4 border border-slate-200">
                  <span className="w-2 h-2 rounded-full bg-[#ff5d22]" style={{ animation: 'srPulse 2s ease infinite' }} />
                  Platform · System Resilience
                </span>
              </Reveal>

              <Reveal delay={0.08}>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.06] text-slate-950 mb-4">
                  Engineered for{' '}
                  <span className="bg-gradient-to-r from-[#ff5d22] to-red-500 bg-clip-text text-transparent">
                    100% durability
                  </span>
                </h1>
              </Reveal>

              <Reveal delay={0.12}>
                <p className="text-sm text-slate-600 font-medium leading-relaxed max-w-lg mb-6">
                  Three architectural pillars that keep AppGallop operating with zero compromise — from event-driven workflow orchestration and real-time API provisioning to transaction-level financial integrity.
                </p>
              </Reveal>

              {/* live status pills */}
              <Reveal delay={0.15}>
                <div className="flex flex-wrap gap-2 mb-6">
                  {[
                  { label: 'Order automation', value: '95%', color: 'emerald' },
                    { label: 'Provisioning speed', value: '<60 sec', color: 'blue' },
                    { label: 'Revenue accuracy', value: '100%', color: 'red' },
                  ].map((p, i) => (
                    <div key={i} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-[10px] font-black ${
                      p.color === 'emerald' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
                      p.color === 'blue' ? 'bg-blue-50 border-blue-200 text-blue-700' :
                      'bg-[#ff5d22]/8 border-[#E89951]/25 text-[#E89951]'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        p.color === 'emerald' ? 'bg-emerald-500' : p.color === 'blue' ? 'bg-blue-500' : 'bg-[#E89951]'
                      }`} style={{ animation: 'srPulse 2.5s ease infinite' }} />
                      <span className="opacity-60 font-bold">{p.label}</span>
                      <span>{p.value}</span>
                    </div>
                  ))}
                </div>
              </Reveal>

              {/* jump-to spec cards */}
              <Reveal delay={0.18}>
                <div className="space-y-1.5">
                  {SPECS.map((s, i) => (
                    <a key={s.slug} href={`#${s.slug}`}
                      className="group flex items-center gap-3 p-2.5 rounded-xl border border-black/8 bg-white/80 hover:border-[#ff5d22]/30 hover:shadow-md hover:shadow-[#ff5d22]/6 transition-all duration-300">
                      <div className="w-8 h-8 rounded-lg bg-[#ff5d22]/8 border border-[#ff5d22]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#ff5d22] group-hover:border-[#ff5d22] transition-all duration-300">
                        <s.icon size={15} className="text-[#ff5d22] group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div className="flex-grow min-w-0">
                        <div className="text-[8px] font-black uppercase tracking-widest text-black/30 mb-0.5">Spec {String(i + 1).padStart(2, '0')}</div>
                        <div className="text-xs font-black text-black group-hover:text-[#ff5d22] transition-colors">{s.title}</div>
                        <div className="text-[10px] text-black/40 font-medium truncate">{s.tagline}</div>
                      </div>
                      <div className="flex-shrink-0 text-[8px] font-black uppercase tracking-widest text-[#ff5d22]/50 group-hover:text-[#ff5d22] transition-colors flex items-center gap-1">
                        View <ArrowRight size={10} />
                      </div>
                    </a>
                  ))}
                </div>
              </Reveal>
            </div>

            {/* RIGHT: mini architecture diagram */}
            <Reveal delay={0.14}>
              <div className="relative">
                {/* outer glow */}
                <div className="absolute -inset-4 bg-gradient-to-br from-[#ff5d22]/8 to-blue-500/5 blur-2xl rounded-3xl" />

                <div className="relative bg-slate-950 rounded-3xl border border-white/10 overflow-hidden p-5 shadow-2xl">
                  {/* top bar */}
                  <div className="flex items-center justify-between mb-5 pb-4 border-b border-white/8">
                    <div>
                      <div className="text-[9px] font-black uppercase tracking-[0.2em] text-white/40 mb-1">AppGallop Resilience Stack</div>
                      <div className="text-sm font-black text-white">Architecture Overview</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" style={{ animation: 'srPulse 2s ease infinite' }} />
                      <span className="text-[9px] font-black text-emerald-400">All Systems Operational</span>
                    </div>
                  </div>

                  {/* pipeline nodes */}
                  <div className="space-y-2.5 mb-5">
                    {[
                      { icon: Activity, label: 'Lifecycle Engine', sub: 'BPM · L2O · O2Q · T2R', pct: 94, color: '#ff5d22' },
                      { icon: Zap,      label: 'Service Orchestration', sub: 'API Gateway · 50+ Connectors', pct: 88, color: '#3b82f6' },
                      { icon: Shield,   label: 'Financial Reconciliation', sub: 'GL Mapping · ASC 606 · IFRS 15', pct: 100, color: '#10b981' },
                    ].map((node, i) => (
                      <div key={i} className="rounded-xl bg-white/5 border border-white/8 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${node.color}20`, border: `1px solid ${node.color}40` }}>
                              <node.icon size={13} style={{ color: node.color }} />
                            </div>
                            <div>
                              <div className="text-[11px] font-black text-white">{node.label}</div>
                              <div className="text-[9px] text-white/40 font-medium">{node.sub}</div>
                            </div>
                          </div>
                          <span className="text-[10px] font-black" style={{ color: node.color }}>{node.pct}%</span>
                        </div>
                        <div className="h-1 w-full rounded-full bg-white/10 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${node.pct}%`, background: node.color, boxShadow: `0 0 8px ${node.color}60` }} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* bottom stats row */}
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { v: '50k+', l: 'Concurrent flows' },
                      { v: '<2s', l: 'Step execution' },
                      { v: 'SOC 2', l: 'Type II certified' },
                    ].map((s, i) => (
                      <div key={i} className="rounded-xl bg-white/4 border border-white/8 p-3 text-center">
                        <div className="text-base font-black text-[#ff5d22]">{s.v}</div>
                        <div className="text-[8px] text-white/40 font-bold uppercase tracking-wider mt-0.5">{s.l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-4 rounded-2xl border border-[#ff5d22]/20 bg-gradient-to-r from-[#ff5d22]/5 to-red-500/5 p-4">
  <div className="text-[10px] font-black uppercase tracking-widest text-[#ff5d22] mb-1">
    Ready to see it in action?
  </div>

  <div className="text-xs text-slate-600 mb-3">
    Explore how AppGallop automates cloud commerce, provisioning, and revenue operations.
  </div>

  <Link
    to="/contact"
    className="group flex items-center justify-center gap-2 rounded-xl bg-[#ff5d22] px-5 py-3 text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#d41820] transition-all"
  >
    Book a Demo
    <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
  </Link>
</div>
            </Reveal>
          </div>

          {/* ── bottom trust strip ── */}
          <Reveal delay={0.22}>
            <div className="flex flex-wrap items-center gap-3 pt-6 border-t border-black/6">
              <span className="text-[9px] font-black uppercase tracking-widest text-black/30 mr-1">Compliant with</span>
              {['SOC 2 Type II', 'ISO 27001', 'ASC 606', 'IFRS 15', 'GDPR', '160+ Tax Regimes'].map(badge => (
                <span key={badge} className="px-3 py-1 rounded-full bg-slate-100 border border-slate-200 text-[9px] font-black uppercase tracking-wider text-slate-500">
                  {badge}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Sticky nav ── */}
      <StickyNav active={activeSlug} />

      {/* ── Spec sections ── */}
      {SPECS.map((s, i) => <SpecSection key={s.slug} spec={s} idx={i} />)}

      {/* ── CTA ── */}
      <section className="relative bg-slate-950 py-20 px-6 overflow-hidden">
        <ParticleCanvas color="#ff5d22" count={50} />
        <Orb style={{ top: '10%', left: '8%' }} color="#ff5d22" size={400} dur={20} />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-white/70 text-[9px] font-black uppercase tracking-widest mb-5">
              Built for cloud distributors
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl font-black tracking-tighter text-white mb-4">
              Cloud Commerce.{' '}
              <span className="bg-gradient-to-r from-[#ff5d22] to-red-400 bg-clip-text text-transparent">
                Powered End-to-End.
              </span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="text-white/65 text-base leading-relaxed mb-8 max-w-xl mx-auto">
              From customer acquisition and partner onboarding to cloud service activation, subscription management, billing, and financial reconciliation, AppGallop provides a unified platform for modern cloud distribution.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/contact" className="px-8 py-3.5 rounded-lg bg-[#ff5d22] text-white text-[10px] font-black uppercase tracking-widest hover:bg-[#d41820] transition-all shadow-xl hover:shadow-[#ff5d22]/25 flex items-center gap-2">
                Book a Demo <ArrowRight size={14} />
              </Link>              
            </div>
          </Reveal>
        </div>
      </section>

      <ContactSection id="contact" />
    </div>
  );
}
